import { useEffect, useState } from 'react';

export default function HomePage() {
  const [charCount, setCharCount] = useState(0);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const textarea = document.getElementById('address') as HTMLTextAreaElement;
    if (textarea) {
      const updateCount = () => {
        setCharCount(textarea.value.length);
      };
      textarea.addEventListener('input', updateCount);
      return () => textarea.removeEventListener('input', updateCount);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    
    const data = {
      name: formData.get('name'),
      phone: formData.get('phone'),
      address: formData.get('address'),
      book: formData.get('book')
    };

    try {
      const response = await fetch('https://readdy.ai/api/form/d57rmjvahj0hkf71i130', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(data as any).toString()
      });

      if (response.ok) {
        alert('신청이 완료되었습니다. 감사합니다!');
        form.reset();
        setCharCount(0);
      } else {
        alert('신청 중 오류가 발생했습니다. 다시 시도해주세요.');
      }
    } catch (error) {
      alert('신청 중 오류가 발생했습니다. 다시 시도해주세요.');
    }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center px-6 py-20 bg-gradient-to-br from-emerald-900 via-emerald-800 to-stone-800 overflow-hidden">
        {/* Navigation Bar */}
        <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrollY > 50 ? 'bg-white shadow-md' : 'bg-transparent'}`}>
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center gap-3">
              {/* Logo Image */}
              <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                <img 
                  src="https://static.readdy.ai/image/978f3255162089bd9a3c930a82c95049/1d17c2b718a80dc837f4e22bff4ccca2.png" 
                  alt="행크연구소 로고" 
                  className="w-full h-full object-contain"
                />
              </div>
              
              {/* Brand Name */}
              <div className="flex flex-col">
                <span className={`text-xl font-bold tracking-tight ${scrollY > 50 ? 'text-emerald-800' : 'text-white'}`}>
                  행크연구소
                </span>
              </div>
            </div>
          </div>
        </nav>

        {/* Parallax Background Image */}
        <div 
          className="absolute inset-0 bg-[url('https://public.readdy.ai/ai/img_res/e21fa42b8556b28f0535bc5a890dafaa.jpg')] bg-cover bg-center"
          style={{
            transform: `translateY(${scrollY * 0.5}px)`,
            opacity: 0.25
          }}
        ></div>
        
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>

        {/* Falling Snow Animation */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {[...Array(80)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-snowfall"
              style={{
                left: `${Math.random() * 100}%`,
                top: `-${Math.random() * 20}%`,
                animationDelay: `${Math.random() * 8}s`,
                animationDuration: `${8 + Math.random() * 12}s`,
                width: `${2 + Math.random() * 4}px`,
                height: `${2 + Math.random() * 4}px`,
              }}
            >
              <div className="w-full h-full bg-white rounded-full opacity-80 shadow-lg"></div>
            </div>
          ))}
        </div>

        {/* Glowing Cross - Moved Above Title */}
        <div className="absolute top-24 left-1/2 transform -translate-x-1/2 z-20">
          <div className="relative">
            <div className="absolute inset-0 blur-2xl bg-yellow-300 opacity-60 animate-pulse"></div>
            <i className="ri-cross-fill text-6xl text-yellow-200 relative z-10 drop-shadow-2xl"></i>
          </div>
        </div>
        
        <div className="relative z-10 max-w-3xl mx-auto text-center pt-32">
          {/* Updated Headline */}
          <h1 className="text-4xl md:text-5xl font-bold mb-8 text-stone-50 leading-relaxed animate-fade-in">
            행동하는 크리스천
          </h1>
          
          {/* Subtitle with HANK Emphasis */}
          <p className="text-xl md:text-2xl text-stone-200 mb-16 animate-fade-in font-light tracking-wide">
            <span className="text-rose-500 font-bold">H</span>olistic{' '}
            <span className="text-rose-500 font-bold">A</span>ction for{' '}
            the{' '}
            <span className="text-rose-500 font-bold">N</span>ext{' '}
            <span className="text-rose-500 font-bold">K</span>orea
          </p>
          
          <div className="space-y-8 text-stone-50 leading-relaxed text-base animate-slide-up">
            <p className="text-xl font-medium">행크연구소 후원자님,</p>
            
            <p>대통령 비상계엄과 탄핵이라는 풍랑 속에서 시작한 2025년이<br />어느덧 마무리되고 있습니다.</p>
            
            <p>그 어느 때보다 대한민국과 다음 세대를 위해<br />많은 성도들이 눈물로 기도한 한 해였습니다.</p>
            
            <p>나라가 어려운 시간을 지나고 있지만,<br />함께 기도하며 뜻을 나눌 수 있는 동역자가 계심에<br />진심으로 감사한 마음입니다.</p>
            
            <p>이 나라와 민족을 향한 후원자님의 헌신과 기도에<br />하나님은 반드시 응답하실 것입니다.</p>
            
            <p>모든 것을 합력하여 선을 이루시는 하나님께서<br />역사의 시곗바늘을 돌리시고 있음을 믿습니다.</p>
            
            <p>대한민국을 향한 하나님의 부르심이<br />반드시 이루어질 것을 믿습니다.</p>
            
            <p>우리 안의 우상이 깨어지고<br />오직 하나님만 의지하도록<br />우리의 믿음을 새롭게 하시길 기도합니다.</p>
            
            <p className="text-lg font-medium pt-4">주 안에서 따뜻한 연말 되시길 바랍니다.</p>
            
            <p className="text-2xl font-bold pt-6">"하나님이 보우하사, 우리나라 만세!"</p>
            
            <p className="text-lg pt-8">행크연구소 드림.</p>
          </div>
        </div>
      </section>

      {/* 2025년 활동 하이라이트 */}
      <section className="py-24 px-6 bg-gradient-to-b from-stone-50 to-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-4 text-stone-800">2025년 활동 하이라이트</h2>
          <p className="text-center text-stone-600 mb-16 text-base">후원자님의 소중한 후원으로 이루어낸 성과입니다</p>
          
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* 행크연구소 채널 */}
            <div className="bg-gradient-to-br from-rose-50 to-rose-100 rounded-lg p-8 shadow-md hover:shadow-lg transition-shadow border border-rose-200">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 flex items-center justify-center bg-rose-600 rounded-full">
                  <i className="ri-youtube-fill text-2xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-stone-800">행크연구소 채널</h3>
              </div>
              
              <div className="space-y-4 mb-6">
                <div className="flex justify-between items-center">
                  <span className="text-stone-600 text-sm">업로드 영상</span>
                  <span className="text-3xl font-bold text-rose-600">169개</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-stone-600 text-sm">누적 조회수</span>
                  <span className="text-3xl font-bold text-rose-600">1,053,627회</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-stone-600 text-sm">구독자 증가</span>
                  <span className="text-3xl font-bold text-rose-600">+14,000명</span>
                </div>
              </div>
              
              <a 
                href="https://www.youtube.com/@hanklab" 
                target="_blank" 
                rel="noopener noreferrer"
                className="block w-full bg-rose-600 text-white text-center py-3 rounded-lg font-medium hover:bg-rose-700 transition-colors whitespace-nowrap cursor-pointer text-sm"
              >
                채널 바로가기
              </a>
            </div>

            {/* 행크뉴스 채널 */}
            <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-lg p-8 shadow-md hover:shadow-lg transition-shadow border border-emerald-200">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 flex items-center justify-center bg-emerald-600 rounded-full">
                  <i className="ri-youtube-fill text-2xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-stone-800">행크뉴스 채널</h3>
              </div>
              
              <div className="space-y-4 mb-6">
                <div className="flex justify-between items-center">
                  <span className="text-stone-600 text-sm">업로드 영상</span>
                  <span className="text-3xl font-bold text-emerald-600">20개</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-stone-600 text-sm">누적 조회수</span>
                  <span className="text-3xl font-bold text-emerald-600">3,298,396회</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-stone-600 text-sm">구독자 증가</span>
                  <span className="text-3xl font-bold text-emerald-600">+19,000명</span>
                </div>
              </div>
              
              <a 
                href="https://www.youtube.com/@hanknews" 
                target="_blank" 
                rel="noopener noreferrer"
                className="block w-full bg-emerald-600 text-white text-center py-3 rounded-lg font-medium hover:bg-emerald-700 transition-colors whitespace-nowrap cursor-pointer text-sm"
              >
                채널 바로가기
              </a>
            </div>
          </div>

          {/* 칼럼 및 기고 */}
          <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg p-8 shadow-md border border-amber-200">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 flex items-center justify-center bg-amber-600 rounded-full">
                <i className="ri-article-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-stone-800">칼럼 및 기고 활동</h3>
            </div>
            
            <div className="mb-4">
              <p className="text-stone-700 mb-4 text-base">
                성경적 정치관·기독교 세계관 관련 칼럼 <strong className="text-amber-700 text-2xl">총 10건</strong>
              </p>
              
              <div className="flex flex-wrap gap-3">
                <span className="px-4 py-2 bg-white rounded-full text-stone-700 text-sm font-medium shadow-sm">크리스천투데이</span>
                <span className="px-4 py-2 bg-white rounded-full text-stone-700 text-sm font-medium shadow-sm">복음기도신문</span>
                <span className="px-4 py-2 bg-white rounded-full text-stone-700 text-sm font-medium shadow-sm">월드뷰</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2025년 주요 프로젝트 */}
      <section className="py-24 px-6 bg-gradient-to-b from-white to-emerald-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-4 text-stone-800">2025년 주요 프로젝트</h2>
          <p className="text-center text-stone-600 mb-16 text-base">말이 아니라 실제 행동으로 보여드렸습니다</p>
          
          <div className="grid md:grid-cols-2 gap-8">
            {/* 교회자유수호연대 */}
            <div className="bg-white rounded-lg p-8 shadow-md hover:shadow-lg transition-shadow border-l-4 border-emerald-600">
              <div className="mb-6">
                <div className="w-14 h-14 flex items-center justify-center bg-emerald-600 rounded-lg mb-4">
                  <i className="ri-shield-check-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-stone-800 mb-3">교회자유수호연대 사이트 런칭</h3>
                <a 
                  href="https://church-freedom.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-emerald-600 hover:text-emerald-700 font-medium text-sm cursor-pointer"
                >
                  church-freedom.com →
                </a>
              </div>
              
              <div className="space-y-3 mb-6">
                <p className="text-stone-700 text-sm">
                  <strong>부산 세계로교회 손현보 목사</strong> 구속 반대 서명운동
                </p>
                <div className="flex items-center gap-2 text-stone-600 text-sm">
                  <i className="ri-calendar-line"></i>
                  <span>구속수감 D+110일 (12/27 기준)</span>
                </div>
                <div className="flex items-center gap-2 text-emerald-600 font-bold text-base">
                  <i className="ri-user-line"></i>
                  <span>서명 7,421명 돌파</span>
                </div>
                <div className="flex items-center gap-2 text-stone-600 text-sm">
                  <i className="ri-chat-3-line"></i>
                  <span>댓글 1,000건 이상</span>
                </div>
              </div>
            </div>

            {/* 위체인지 */}
            <div className="bg-white rounded-lg p-8 shadow-md hover:shadow-lg transition-shadow border-l-4 border-rose-600">
              <div className="mb-6">
                <div className="w-14 h-14 flex items-center justify-center bg-rose-600 rounded-lg mb-4">
                  <i className="ri-hand-heart-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-stone-800 mb-3">위체인지 (WE-CHANGE) 플랫폼</h3>
                <a 
                  href="https://we-change.kr" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-rose-600 hover:text-rose-700 font-medium text-sm cursor-pointer"
                >
                  we-change.kr →
                </a>
              </div>
              <p className="text-stone-700 text-sm">악법 반대를 위한 시민 청원 플랫폼</p>
            </div>

            {/* 그리스도를 위해 투표하라 */}
            <div className="bg-white rounded-lg p-8 shadow-md hover:shadow-lg transition-shadow border-l-4 border-amber-600">
              <div className="mb-6">
                <div className="w-14 h-14 flex items-center justify-center bg-amber-600 rounded-lg mb-4">
                  <i className="ri-checkbox-circle-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-stone-800 mb-3">그리스도를 위해 투표하라</h3>
                <a 
                  href="https://voteforchrist.kr" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-amber-600 hover:text-amber-700 font-medium text-sm cursor-pointer"
                >
                  voteforchrist.kr →
                </a>
              </div>
              <p className="text-stone-700 text-sm">기독교 투표 독려 운동 플랫폼</p>
            </div>

            {/* 세이브코리아 */}
            <div className="bg-white rounded-lg p-8 shadow-md hover:shadow-lg transition-shadow border-l-4 border-teal-600">
              <div className="mb-6">
                <div className="w-14 h-14 flex items-center justify-center bg-teal-600 rounded-lg mb-4">
                  <i className="ri-flag-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-stone-800 mb-3">세이브코리아 웹사이트 런칭</h3>
              </div>
              <p className="text-stone-700 text-sm">대한민국을 지키기 위한 시민운동 플랫폼</p>
            </div>

            {/* 행동하는크리스천 기도 네트워크 */}
            <div className="bg-white rounded-lg p-8 shadow-md hover:shadow-lg transition-shadow border-l-4 border-violet-600 md:col-span-2">
              <div className="mb-6">
                <div className="w-14 h-14 flex items-center justify-center bg-violet-600 rounded-lg mb-4">
                  <i className="ri-team-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-stone-800 mb-3">행동하는크리스천 기도 네트워크</h3>
                <p className="text-sm text-stone-600 mb-3">(가칭: CIPA)</p>
                
                <div className="space-y-2">
                  <a 
                    href="https://vfcindex.netlify.app/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="block text-violet-600 hover:text-violet-700 font-medium text-sm cursor-pointer"
                  >
                    버전1: vfcindex.netlify.app →
                  </a>
                  <a 
                    href="https://codepen.io/Seonbeom-JANG/full/GgqgdgV" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="block text-violet-600 hover:text-violet-700 font-medium text-sm cursor-pointer"
                  >
                    버전2: codepen.io →
                  </a>
                </div>
              </div>
              
              <div className="flex items-center gap-2 text-stone-600 text-sm">
                <span className="px-3 py-1 bg-violet-100 text-violet-700 rounded-full text-xs font-medium">개발 진행중</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 행크연구소 소개 */}
      <section className="py-24 px-6 bg-gradient-to-b from-emerald-50 to-white relative overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 bg-[url('https://static.readdy.ai/image/a49bc9fdcf82a218c47af874f67f4a4f/9f90de2e37554ec71496768df458817b.jpeg')] bg-cover bg-center opacity-10"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-50/90 via-white/95 to-white/90"></div>
        
        <div className="max-w-4xl mx-auto relative z-10">
          <h2 className="text-4xl font-bold text-center mb-16 text-stone-800">행크연구소 소개</h2>
          
          <div className="space-y-8 text-stone-700 leading-relaxed mb-12 text-base text-center">
            <p className="text-xl font-medium text-stone-800">행동하는 크리스천, 행크연구소입니다.</p>
            
            <p>성경의 진리가 불법이 되는 시대를 살고 있습니다.<br />반기독교적 법이 제정되면 교회는 급속히 타락합니다.</p>
            
            <p>교회를 지키지 못하면<br />다음 세대에는 신앙의 자유가 사라집니다.</p>
            
            <p className="text-lg font-bold text-emerald-700">기도와 행동으로<br />교회의 가치를 사수하겠습니다.</p>
          </div>
          
          {/* 대표 소개 */}
          <div className="bg-white/90 backdrop-blur-sm rounded-lg p-8 mb-12 shadow-md border border-emerald-200">
            <h3 className="text-2xl font-bold text-center mb-8 text-stone-800">대표 소개</h3>
            
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="flex-shrink-0">
                <img 
                  src="https://static.readdy.ai/image/a49bc9fdcf82a218c47af874f67f4a4f/9f90de2e37554ec71496768df458817b.jpeg" 
                  alt="장선범 목사" 
                  className="w-48 h-48 object-cover rounded-lg shadow-md"
                />
              </div>
              
              <div className="flex-1 text-center md:text-left">
                <h4 className="text-2xl font-bold text-stone-800 mb-3">장선범 목사</h4>
                <div className="space-y-2 text-stone-700 text-sm leading-relaxed">
                  <p>한동대학교 졸업 후 중동지역 선교사가 되었다.</p>
                  <p>이후 사우스웨스턴침례신학교를 졸업하고, 현재 목사로서 지역 교회를 섬기며 다음세대 신앙의 자유를 위해 행크연구소(행동하는크리스천)를 운영하고 있다.</p>
                  <p className="pt-3">
                    <a 
                      href="mailto:jsb@hankkorea.org" 
                      className="text-emerald-600 hover:text-emerald-700 font-medium cursor-pointer"
                    >
                      jsb@hankkorea.org
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-emerald-100 to-teal-100 rounded-lg p-10 shadow-md border border-emerald-200">
            <h3 className="text-2xl font-bold text-center mb-8 text-stone-800">행크연구소가 하는 일</h3>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-emerald-600 rounded-lg flex-shrink-0">
                  <i className="ri-file-list-3-line text-xl text-white"></i>
                </div>
                <div>
                  <p className="font-medium text-stone-800 text-sm">반기독교적 법·정책 분석 및 대응</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-emerald-600 rounded-lg flex-shrink-0">
                  <i className="ri-book-open-line text-xl text-white"></i>
                </div>
                <div>
                  <p className="font-medium text-stone-800 text-sm">기독교 세계관 연구 및 교육 콘텐츠 제작</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-emerald-600 rounded-lg flex-shrink-0">
                  <i className="ri-shield-star-line text-xl text-white"></i>
                </div>
                <div>
                  <p className="font-medium text-stone-800 text-sm">신앙의 자유 수호 활동</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-emerald-600 rounded-lg flex-shrink-0">
                  <i className="ri-global-line text-xl text-white"></i>
                </div>
                <div>
                  <p className="font-medium text-stone-800 text-sm">온라인 지식 플랫폼 개발</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2026년 후원 요청 */}
      <section className="py-24 px-6 bg-gradient-to-b from-white to-rose-50">
        <div className="max-w-3xl mx-auto">
          {/* 편지 스타일 카드 */}
          <div className="bg-white rounded-lg shadow-xl border-2 border-rose-200 p-12 md:p-16 relative overflow-hidden">
            {/* 은은한 크리스마스 장식 */}
            <div className="absolute top-0 right-0 w-32 h-32 opacity-5">
              <i className="ri-cross-fill text-9xl text-emerald-600"></i>
            </div>
            <div className="absolute bottom-0 left-0 w-24 h-24 opacity-5">
              <i className="ri-cross-fill text-7xl text-emerald-600"></i>
            </div>
            
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-bold mb-8 text-stone-800 text-center border-b-2 border-emerald-600 pb-6">
                2026년에도 함께해 주세요
              </h2>
              
              <div className="space-y-6 text-stone-700 leading-relaxed mb-12 text-base md:text-lg">
                <p>2026년에도 행크연구소가</p>
                <p>기도하고, 연구하고, 행동할 수 있도록</p>
                <p>변함없는 동행을 부탁드립니다.</p>
              </div>
              
              <div className="bg-gradient-to-br from-stone-50 to-emerald-50 rounded-lg p-8 md:p-10 mb-8 border-2 border-emerald-200">
                <h3 className="text-2xl font-bold mb-8 text-stone-800 text-center">후원 정보</h3>
                
                <div className="space-y-8">
                  {/* 국내 후원 */}
                  <div className="space-y-6">
                    <h4 className="text-lg font-bold text-stone-800 border-b border-emerald-300 pb-2">국내 후원</h4>
                    
                    <div className="bg-white rounded-lg p-6 border-2 border-emerald-600 shadow-sm">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 flex items-center justify-center bg-emerald-600 rounded-lg">
                          <i className="ri-bank-card-line text-xl text-white"></i>
                        </div>
                        <h5 className="font-bold text-lg text-stone-800">계좌 후원</h5>
                      </div>
                      <p className="text-lg font-bold text-stone-800 mb-1">국민은행 821701-01-673838</p>
                      <p className="text-sm text-stone-600">(행크연구소)</p>
                    </div>
                    
                    <div className="bg-white rounded-lg p-6 border-2 border-emerald-600 shadow-sm">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 flex items-center justify-center bg-emerald-600 rounded-lg">
                          <i className="ri-heart-line text-xl text-white"></i>
                        </div>
                        <h5 className="font-bold text-lg text-stone-800">정기후원</h5>
                      </div>
                      <a 
                        href="https://cherry.charity/public/campaign/cmpgnDtlPage/13057" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-block mt-2 px-6 py-3 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors whitespace-nowrap cursor-pointer text-sm"
                      >
                        정기후원 신청하기 →
                      </a>
                      <p className="text-sm text-stone-600 mt-3">기부금 영수증 발급 가능</p>
                    </div>
                  </div>

                  {/* 해외 후원 */}
                  <div className="space-y-6">
                    <h4 className="text-lg font-bold text-stone-800 border-b border-blue-300 pb-2">해외 후원 (Overseas Support)</h4>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <a 
                        href="https://www.venmo.com/u/cia_lab" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="bg-white rounded-lg p-6 border-2 border-blue-500 shadow-sm hover:shadow-md transition-shadow cursor-pointer block"
                      >
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 flex items-center justify-center bg-blue-500 rounded-lg">
                            <i className="ri-wallet-3-line text-xl text-white"></i>
                          </div>
                          <h5 className="font-bold text-base text-stone-800">Venmo (USA)</h5>
                        </div>
                        <p className="text-sm text-stone-600">미국 간편 송금</p>
                      </a>

                      <a 
                        href="http://buymeacoffee.com/cia_lab" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="bg-white rounded-lg p-6 border-2 border-orange-500 shadow-sm hover:shadow-md transition-shadow cursor-pointer block"
                      >
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 flex items-center justify-center bg-orange-500 rounded-lg">
                            <i className="ri-cup-line text-xl text-white"></i>
                          </div>
                          <h5 className="font-bold text-base text-stone-800">Buy Me a Coffee</h5>
                        </div>
                        <p className="text-sm text-stone-600">글로벌 후원</p>
                      </a>
                    </div>
                  </div>

                  {/* 기도실 */}
                  <div className="space-y-4">
                    <h4 className="text-lg font-bold text-stone-800 border-b border-yellow-300 pb-2">함께 기도해요</h4>
                    
                    <a 
                      href="https://open.kakao.com/o/gwaUM53c" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="block bg-[#FEE500] rounded-lg p-6 border-2 border-[#FEE500] shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 flex items-center justify-center bg-[#3C1E1E] rounded-lg">
                          <i className="ri-chat-3-fill text-xl text-[#FEE500]"></i>
                        </div>
                        <div>
                          <h5 className="font-bold text-base text-[#3C1E1E]">행크 다음세대 기도실</h5>
                          <p className="text-sm text-[#3C1E1E]/80">(오픈채팅)</p>
                        </div>
                      </div>
                    </a>
                  </div>
                </div>
              </div>
              
              <p className="text-xl md:text-2xl font-bold text-center text-stone-800 pt-6">
                기도하고 행동하면, 기적이 일어납니다.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 후원자 감사 선물 신청 */}
      <section className="py-24 px-6 bg-gradient-to-b from-rose-50 to-amber-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-6 text-stone-800">후원자 감사 선물 신청</h2>
          <p className="text-center text-stone-600 mb-12 text-base">
            행크연구소에서 감사의 마음을 담아<br />
            후원자님께 선물을 보내드립니다.<br />
            아래 두 권의 신간 중 한 권을 선택해 주세요.
          </p>
          
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* 손현보 목사 저서 */}
            <a 
              href="https://product.kyobobook.co.kr/detail/S000218607814" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-white rounded-lg p-8 shadow-md border-2 border-stone-200 hover:border-rose-500 transition-colors cursor-pointer block"
            >
              <div className="aspect-[3/4] bg-gradient-to-br from-stone-200 to-stone-300 rounded-lg mb-6 flex items-center justify-center overflow-hidden">
                <img 
                  src="https://static.readdy.ai/image/978f3255162089bd9a3c930a82c95049/7434a22164137b72aab582cfab6bd756.jpeg" 
                  alt="손현보 목사 저서" 
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="text-xl font-bold text-stone-800 mb-3">손현보 목사 저서 (부산세계로교회 담임)</h3>
              <ul className="space-y-2 text-stone-600 text-sm">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-rose-600 mt-1"></i>
                  <span>부산교육감 선거법 위반 혐의로 구속 수감 중 집필</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-rose-600 mt-1"></i>
                  <span>구속 경위와 신앙 고백, 눈물의 간증 수록</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-rose-600 mt-1"></i>
                  <span>불의한 구속수감 110일째 (12/27기준)</span>
                </li>
              </ul>
            </a>

            {/* 김미영 변호사 저서 */}
            <a 
              href="https://product.kyobobook.co.kr/detail/S000218675833" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-white rounded-lg p-8 shadow-md border-2 border-stone-200 hover:border-rose-500 transition-colors cursor-pointer block"
            >
              <div className="aspect-[3/4] bg-gradient-to-br from-stone-200 to-stone-300 rounded-lg mb-6 flex items-center justify-center overflow-hidden">
                <img 
                  src="https://static.readdy.ai/image/978f3255162089bd9a3c930a82c95049/6ef1fb701838d9e31451de3ff756c766.jpeg" 
                  alt="김미영 변호사 저서" 
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="text-xl font-bold text-stone-800 mb-3">김미영 변호사 저서 (VON 대표)</h3>
              <ul className="space-y-2 text-stone-600 text-sm">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-rose-600 mt-1"></i>
                  <span>대한민국 자유민주주의 시스템 붕괴를 100가지 팩트로 분석</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-rose-600 mt-1"></i>
                  <span>북한인권 운동에 오랜 시간 헌신한 저자의 연구</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-rose-600 mt-1"></i>
                  <span>완전히 밝혀진 국가 선거시스템 조작의 실체</span>
                </li>
              </ul>
            </a>
          </div>

          {/* 선물 신청 폼 */}
          <form 
            data-readdy-form 
            id="gift-form" 
            className="bg-white rounded-lg p-10 shadow-lg border-2 border-amber-200"
            onSubmit={handleSubmit}
          >
            <h3 className="text-2xl font-bold text-stone-800 mb-4 text-center">후원자 도서선물 신청서</h3>
            
            <div className="bg-amber-50 rounded-lg p-4 mb-8 border border-amber-200">
              <ul className="space-y-2 text-sm text-stone-700">
                <li className="flex items-start gap-2">
                  <i className="ri-information-line text-amber-600 mt-0.5"></i>
                  <span>도서 재고가 소진되면 다른 책으로 발송합니다.</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-information-line text-amber-600 mt-0.5"></i>
                  <span>본 선물은 2025-2026년 행크연구소 후원자를 대상으로 합니다.</span>
                </li>
              </ul>
            </div>
            
            <div className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-stone-700 mb-2">
                  이름 <span className="text-red-500">*</span>
                </label>
                <input 
                  type="text"
                  id="name"
                  name="name"
                  required
                  className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                  placeholder="이름을 입력해주세요"
                />
              </div>
              
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-stone-700 mb-2">
                  연락처 <span className="text-red-500">*</span>
                </label>
                <input 
                  type="tel"
                  id="phone"
                  name="phone"
                  required
                  className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                  placeholder="010-0000-0000"
                />
              </div>
              
              <div>
                <label htmlFor="address" className="block text-sm font-medium text-stone-700 mb-2">
                  주소 <span className="text-red-500">*</span>
                </label>
                <textarea 
                  id="address"
                  name="address"
                  required
                  rows={3}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm resize-none"
                  placeholder="도서를 받으실 주소를 입력해주세요"
                  onChange={(e) => setCharCount(e.target.value.length)}
                ></textarea>
                <p className="text-xs text-stone-500 mt-1">{charCount}/500자</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-3">
                  도서 선택 <span className="text-red-500">*</span>
                </label>
                <div className="space-y-3">
                  <label className="flex items-start gap-3 p-4 border-2 border-stone-200 rounded-lg hover:border-emerald-500 cursor-pointer transition-colors">
                    <input 
                      type="radio"
                      name="book"
                      value="손현보 목사 저서"
                      required
                      className="mt-1 w-5 h-5 text-emerald-600 cursor-pointer"
                    />
                    <span className="text-sm text-stone-700">손현보 목사 저서</span>
                  </label>
                  
                  <label className="flex items-start gap-3 p-4 border-2 border-stone-200 rounded-lg hover:border-emerald-500 cursor-pointer transition-colors">
                    <input 
                      type="radio"
                      name="book"
                      value="김미영 변호사 저서"
                      required
                      className="mt-1 w-5 h-5 text-emerald-600 cursor-pointer"
                    />
                    <span className="text-sm text-stone-700">김미영 변호사 저서</span>
                  </label>
                </div>
              </div>
              
              <div className="bg-stone-50 rounded-lg p-4 text-xs text-stone-600">
                <p className="font-medium mb-2">개인정보 수집 및 이용 안내</p>
                <p>수집 항목: 이름, 연락처, 주소</p>
                <p>수집 목적: 후원자 감사 선물 발송</p>
                <p>보유 기간: 발송 완료 후 3개월</p>
              </div>
              
              <button 
                type="submit"
                className="w-full bg-emerald-600 text-white py-4 rounded-lg font-bold text-base hover:bg-emerald-700 transition-colors disabled:bg-stone-300 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                선물 신청하기
              </button>
            </div>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-emerald-800 to-emerald-900 text-stone-100 py-12 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <img 
            src="https://static.readdy.ai/image/978f3255162089bd9a3c930a82c95049/1d17c2b718a80dc837f4e22bff4ccca2.png" 
            alt="행크연구소" 
            className="w-20 h-20 mx-auto mb-6 object-contain opacity-90"
          />
          
          <p className="text-sm mb-4 font-medium">행동하는 크리스천, 행크연구소</p>
          <p className="text-xs text-stone-300 mb-6">기도와 행동으로 교회의 가치를 사수합니다</p>
          
          <div className="text-xs text-stone-300">
            <p>© 2025 행크연구소. All rights reserved.</p>
            <a 
              href="https://readdy.ai/?ref=logo" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-block mt-3 text-stone-200 hover:text-white transition-colors cursor-pointer font-medium"
            >
              Holistic Action for the Next Korea
            </a>
          </div>
        </div>
      </footer>

      {/* KakaoTalk Floating Action Button */}
      <a
        href="http://pf.kakao.com/_xgTxlJG/chat"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 w-16 h-16 bg-[#FEE500] rounded-full shadow-lg flex items-center justify-center hover:scale-110 transition-transform duration-300 z-50 cursor-pointer"
        aria-label="카카오톡 채팅 상담"
      >
        <i className="ri-chat-3-fill text-3xl text-[#3C1E1E]"></i>
      </a>
    </div>
  );
}
